// ref: https://reactjs.org/docs/error-boundaries.html
import React from 'react'
import { Trans } from '@lingui/react/macro'

import { IS_DEV } from '@/helpers/env'


import { cls } from 'tagged-classnames-free';
export interface IErrorBoundaryProps {
  onError?: () => void
  /** 完全自定义发生错误时渲染的组件 */
  errorRender?: React.ReactNode
  children: React.ReactNode
}

export interface IErrorBoundaryState {
  hasError: boolean
}

export default class ErrorBoundary extends React.Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  constructor(props: IErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  // https://reactjs.org/docs/react-component.html#componentdidcatch
  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    // console.error('ErrorBoundary:', error.message)
    // 控制台会打印 _error 不必额外打印错误信息，如果需要上报才需要额外处理
  }

  componentDidUpdate(
    _prevProps: IErrorBoundaryProps,
    prevState: IErrorBoundaryState,
  ) {
    const { onError } = this.props
    const { hasError } = this.state

    if (prevState.hasError === false && hasError === true) {
      onError?.()
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-members
  reset = () => {
    this.setState({
      hasError: false,
    })
  }

  render() {
    const { errorRender, children } = this.props
    const { hasError } = this.state
    if (hasError) {
      // 非开发环境支持渲染自定义 Error
      // 避免自定义 Error 导致的异常，比如 Layout 异常导致页面死循环无法显示错误信息
      if (!IS_DEV && errorRender) {
        return errorRender
      }

      // 默认错误渲染组件
      return (
        <div className={cls`bg-red-100 text-center`}>
          <h2 className={'font-bold text-red-600'}>
            <Trans>Oh snap!</Trans>
          </h2>
          <p className={'text-red-400'}>
            <Trans>There was a problem loading this content</Trans>
            {!!errorRender && (
              <>
                ,{' '}
                <Trans>
                  custom `errorRender` but use default errorRender in [DEV]
                </Trans>
              </>
            )}
          </p>
        </div>
      )
    }

    return children
  }
}

export function withErrorBoundary<Props = unknown>(
  WrappedComponent: React.ComponentType<Props>,
  options?: Omit<IErrorBoundaryProps, 'children'>,
) {
  // Ref: https://stackoverflow.com/a/59917277/8335317
  const WithErrorBoundary = React.memo(
    (props: Props & Record<string, never>) => {
      return (
        <ErrorBoundary {...options}>
          <WrappedComponent {...props} />
        </ErrorBoundary>
      )
    },
  )

  WithErrorBoundary.displayName = `WithErrorBoundary(${getDisplayName(
    WrappedComponent,
  )})`

  return WithErrorBoundary
}

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}
